import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getCityBySlug, nationalContact } from '../cities';
import { mockData } from '../mock';
import Header from './Header';
import Hero from './Hero';
import About from './About';
import Services from './Services';
import Projects from './Projects';
import FAQ from './FAQ';
import Contact from './Contact';

const CityPage = () => {
  const { citySlug } = useParams();
  const city = getCityBySlug(citySlug);

  // Scroll nach oben beim Laden der Komponente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [citySlug]);

  // Wenn Region nicht gefunden, redirecte zur Hauptseite
  if (!city) {
    return <Navigate to="/" replace />;
  }

  // Region-spezifische mockData erstellen
  const cityMockData = {
    ...mockData,
    company: {
      ...mockData.company,
      phone: nationalContact.phone,
      phoneDisplay: nationalContact.phoneDisplay,
      phoneDescription: nationalContact.description
    },
    hero: {
      ...mockData.hero,
      title: city.heroTitle,
      subtitle: `${city.description} Bundesweit kostenfrei erreichbar: ${nationalContact.phoneDisplay}`
    }
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <title>{city.name} - Wärmepumpen | NOVATUS Energie</title>
      <meta name="description" content={city.description} />
      <meta name="keywords" content={city.seoKeywords} />
      
      {/* Header (Logo & Navigation) */}
      <Header />
      
      {/* Region-Banner unter dem Header */}
      <div className="bg-gradient-to-r from-[#1A3A2E] to-[#2D4A3E] text-white py-6 shadow-lg pt-28 md:pt-32">
        <div className="container mx-auto px-4">
          {/* Region Info - Zentriert */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-3xl">📍</span>
              <div className="font-bold text-2xl md:text-3xl">{city.name}</div>
            </div>
            <div className="text-base md:text-lg text-[#E8F5E0]/90">
              {city.state} • PLZ: {city.zipCodes}
            </div>
          </div>

          {/* Telefonnummer - Zentriert und hervorgehoben */}
          <div className="flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all shadow-xl">
              <div className="flex items-center gap-4">
                {/* Größeres Telefonhörer-Icon */}
                <div className="bg-white rounded-full p-3 shadow-lg">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-8 h-8 text-[#1A3A2E]" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
                    />
                  </svg>
                </div>
                
                {/* Telefonnummer */}
                <div className="text-left">
                  <a 
                    href={`tel:${nationalContact.phone}`} 
                    className="font-bold text-2xl md:text-3xl hover:text-[#E8F5E0] transition-colors block leading-tight"
                  >
                    {nationalContact.phoneDisplay}
                  </a>
                  <div className="text-sm md:text-base text-[#E8F5E0]/90 mt-1">
                    {nationalContact.description}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Komponenten mit Stadt-spezifischen Daten */}
      <Hero mockData={cityMockData} city={city} />
      
      {/* Stadt-spezifische Info-Box */}
      <div className="bg-[#E8F5E0] py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-[#1A3A2E] mb-4">
              Ihr Wärmepumpen-Experte im Raum {city.name}
            </h2>
            <p className="text-[#1A3A2E]/80 mb-6">
              {city.description} Mit unserer bundesweiten Hotline {nationalContact.phoneDisplay} erreichen Sie uns kostenfrei aus jedem Netz.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-2">🏠</div>
                <div className="font-semibold text-[#1A3A2E]">Regional verankert</div>
                <div className="text-sm text-[#1A3A2E]/70">Vor Ort in {city.region}</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-2">⚡</div>
                <div className="font-semibold text-[#1A3A2E]">Schnelle Reaktion</div>
                <div className="text-sm text-[#1A3A2E]/70">Kurze Anfahrtswege im Raum {city.name}</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-2">🌍</div>
                <div className="font-semibold text-[#1A3A2E]">Bundesweiter Service</div>
                <div className="text-sm text-[#1A3A2E]/70">Teil unseres deutschlandweiten Netzwerks</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <About mockData={cityMockData} />
      <Services mockData={cityMockData} city={city} />
      <Projects mockData={cityMockData} />
      <FAQ mockData={cityMockData} city={city} />
      <Contact mockData={cityMockData} city={city} />

      {/* Weitere Städte in der Region */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-xl font-bold text-[#1A3A2E] mb-4">
              Wir sind auch in Ihrer Nähe
            </h3>
            <p className="text-[#1A3A2E]/70 mb-6">
              Entdecken Sie unsere Standorte in weiteren Städten
            </p>
            <Link
              to="/regionen"
              className="inline-block bg-[#1A3A2E] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#2A4A3E] transition-colors"
            >
              Alle Regionen ansehen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityPage;
